/**
 * frontend/src/api/services.js
 * Centralized API service layer for Guardian Message Check (GMC).
 * Architecture Rules:
 * - Employs a decoupled base HTTP client architecture layer.
 * - Enforces exact property name parity with Pydantic/FastAPI backend schemas.
 * - Strict structural encapsulation: Components manage component states,
 *   services process clean execution input and output pipelines.
 */

import client from './client'

// -- Authentication Endpoints -----------------------------------------

/** Register a new user profile with standard email credentials. */
export const register = (data) =>
    client.post('/auth/register', data)

/** Authenticate an existing user via standard email credentials. */
export const login = (data) =>
    client.post('/auth/login', data)

/** Authenticate or register via Google Federated Identity Provider tokens. */
export const googleLogin = (idToken) =>
    client.post('/auth/google', { id_token: idToken })

/** Retrieve the active authenticated user profile metadata. */
export const getProfile = () =>
    client.get('/auth/me')

/** Trigger a password recovery token emission for the supplied email address. */
export const forgotPassword = (data) =>
    client.post('/auth/forgot-password', data)

/** Submit a verified recovery token alongside a new password replacement. */
export const resetPassword = (data) =>
    client.post('/auth/reset-password', data)

/** Change password while authenticated (requires current + new password). */
export const changePassword = (data) =>
    client.post('/auth/change-password', data)

/**
 * Record acceptance of the onboarding agreement (brief v1.1).
 * Idempotent — accepting twice succeeds and does not move the original
 * timestamp. Access is enforced server-side on the analysis endpoints, so
 * this records consent rather than granting entry.
 */
export const acceptTerms = () =>
    client.post('/auth/accept-terms')


// -- Subscription Plan Endpoints --------------------------------------

/** Fetch the global database registry listing of active plan configurations. */
export const getPlans = () =>
    client.get('/plans')

// NOTE: The immediate platform-level downgrade endpoint (POST /plans/downgrade)
// is intentionally NOT exposed here. User-initiated downgrades must go through
// cancelSubscription() below, which cancels at period end so the user keeps the
// access they have paid for. The immediate-flip endpoint remains on the backend
// for admin/internal use only and must never be wired to the subscription UI.


// -- Payment Processing Endpoints -------------------------------------

/** Initialize a Stripe payment intent for PAYG analysis tracks. */
export const createPaymentIntent = (productType = 'diagnose_rewrite') =>
    client.post('/payments/create-intent', { product_type: productType })

/** Create a Stripe subscription for a recurring plan. */
export const createSubscription = (priceId) =>
    client.post('/payments/create-subscription', { price_id: priceId })

/** Create a Stripe Checkout session for a recurring subscription plan. */
export const createCheckout = (planTier) =>
    client.post('/payments/create-checkout', { plan_tier: planTier })

/** Create a Stripe Checkout session for a one-off PAYG analysis. */
export const createCheckoutPayg = (productType) =>
    client.post('/payments/create-checkout-payg', { product_type: productType })

/** Cancel subscription at the end of the current billing period. */
export const cancelSubscription = () =>
    client.post('/payments/cancel-subscription')

/** Resume a cancelled subscription (undo pending cancellation). */
export const resumeSubscription = () =>
    client.post('/payments/resume-subscription')


// -- Admin Endpoints --------------------------------------------------

/** Get admin dashboard statistics. Requires superuser privileges. */
export const getAdminDashboard = () =>
    client.get('/admin/dashboard')

/** List users with pagination and search. Requires superuser privileges. */
export const getAdminUsers = (params) =>
    client.get('/admin/users', { params })

/** List all subscription plans. Requires superuser privileges. */
export const getAdminPlans = () =>
    client.get('/admin/plans')

/** List recent billing records. Requires superuser privileges. */
export const getAdminBilling = (params) =>
    client.get('/admin/billing', { params })

/** Get system settings. Requires superuser privileges. */
export const getAdminSettings = () =>
    client.get('/admin/settings')

/** Test a URL's SEO metadata (admin SEO console). Requires superuser privileges. */
export const testAdminSeoUrl = (url) =>
    client.post('/admin/seo/test-url', { url })


// -- Analysis History Endpoints ---------------------------------------

/**
 * Fetch the authenticated user's analysis history metadata.
 * Zero retention: returns metadata only, never message content.
 */
export const getHistory = () =>
    client.get('/history')

/**
 * Permanently delete a single analysis history metadata record by ID.
 * @param {string} analysisId - UUID of the analysis record to delete.
 */
export const deleteHistoryRecord = (analysisId) =>
    client.delete(`/history/${analysisId}`)

/**
 * Export the authenticated user's analysis history as a CSV blob.
 * Returns raw response data for client-side blob construction.
 */
export const exportHistoryMetadata = () =>
    client.get('/history/export', { responseType: 'blob' }).then((res) => res.data)


// -- Document Narrative Analysis Endpoints ----------------------------

/**
 * Dispatch message metadata to the analytical evaluation pipeline.
 * Zero Retention: no cleartext message content passes through this endpoint.
 */
export const analyze = (data) =>
    client.post('/analyze', data)


// -- Contact Endpoints ------------------------------------------------

/**
 * Submit a contact enquiry of any category.
 * Writes to the contact_submissions table for manual admin follow-up and
 * returns a customer-facing ticket reference (e.g. GMC-XXXXXXXX).
 *
 * @param {Object} data
 * @param {string} data.name     - Contact full name.
 * @param {string} data.email    - Contact email address.
 * @param {string} data.category - enterprise | support | billing | feedback | other.
 * @param {string} [data.company] - Organisation name. Required for enterprise,
 *                                   must be omitted for all other categories.
 * @param {string} data.message  - Enquiry message body.
 * @returns response.data: { ticket_code, category, created_at }
 */
export const submitContact = (data) =>
    client.post('/contact', data)


// -- Admin: Contact Submissions ---------------------------------------

/** List contact submissions with pagination + optional status/category filters. */
export const getAdminContactSubmissions = (params) =>
    client.get('/admin/contact-submissions', { params })

/** Counts by status and category — powers admin badges/filters. */
export const getAdminContactSubmissionStats = () =>
    client.get('/admin/contact-submissions/stats')

/** Transition a submission's lifecycle status (new | in_progress | resolved). */
export const updateContactSubmissionStatus = (submissionId, newStatus) =>
    client.patch(`/admin/contact-submissions/${submissionId}/status`, { status: newStatus })